import { FunctionComponent, useEffect, useState } from "react";
import { useDebounce } from "react-use";
import { Input, InputProps } from ".";


type NumericInputProps = {
    value?: number | string;
    onChange?: (value?: number) => void;
    required?: boolean;
    allowFloat?: boolean;
    debounce?: number
} & Omit<InputProps, "value" | "onChange">;

function parseValue(value: string, defaultValue: number | undefined, parseAsFloat: boolean): number | undefined {
    const decimalSeparator = (1.1).toLocaleString().charAt(1);
    const americanizedValue = value.replace(decimalSeparator, '.');
    if (parseAsFloat)
        return Number.parseFloat(americanizedValue) ?? defaultValue;
    return Number.parseInt(americanizedValue) ?? defaultValue;
}

export const NumericInput: FunctionComponent<NumericInputProps> = (props) => {
    const { value, onChange, required = false, allowFloat = true, min, max, ...rest } = props;
    const [displayValue, setDisplayValue] = useState<string>("");
    //TODO: Maybe we should add scientific notation if it's over 5 decimals
    const valueAsString = () => (value?.toString());

    // min/max come from InputHTMLAttributes, so they arrive as string | number. They are deliberately
    // NOT forwarded to the <input>: it renders as type="text" (so the browser enforces nothing), and
    // leaving them in the DOM makes a field look bounded when only this clamp bounds it.
    const asNumber = (v: string | number | undefined) => {
        if (v === undefined || v === "") return undefined;
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? n : undefined;
    };
    const minNum = asNumber(min);
    const maxNum = asNumber(max);

    const clamp = (n: number) => {
        const lowered = maxNum !== undefined ? Math.min(n, maxNum) : n;
        return minNum !== undefined ? Math.max(lowered, minNum) : lowered;
    };

    // Clamped on commit rather than on keystroke, so a partially typed number isn't fought as it is
    // entered. Fields with real bounds can't rely on the server rejecting the value afterwards —
    // whatever this commits is what gets saved.
    //
    // The display is rewritten only when the clamp actually overrode the entry, for two reasons.
    // It has to happen at all because the parent may already hold the clamped number (type 400 into a
    // field capped at 365 that is already 365): `value` never changes, the [value] effect never runs,
    // and the box would otherwise sit there showing 400 while 365 is what is stored. And it has to be
    // conditional so that unbounded callers — every other NumericInput in the app — keep their exact
    // current behaviour and don't get "1.50" rewritten to "1.5" mid-typing.
    const commit = (parsed: number) => {
        const clamped = clamp(parsed);
        if (clamped !== parsed) setDisplayValue(clamped.toString());
        if (clamped !== value) onChange?.(clamped);
    };

    useEffect(() => {
        setDisplayValue(valueAsString() ?? "");
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    const onValueChange = (newValue: string) => {
        setDisplayValue(newValue);
    }

    useDebounce(
        () => {
            if (!required && (displayValue === "" || displayValue === undefined)) {
                onChange?.();
                return;
            }
            const parsedVal = parseValue(displayValue, required ? 0 : undefined, allowFloat);
            if (required) {
                if (parsedVal !== null && parsedVal !== undefined && !isNaN(parsedVal)) {
                    commit(parsedVal);
                }
                return
            }

            if (parsedVal !== undefined && !isNaN(parsedVal)) {
                commit(parsedVal);
                return
            }

        },
        props.debounce ?? 300,
        [displayValue]
    );

    return (
        <Input
            value={displayValue}
            onBlur={() => setDisplayValue(valueAsString() ?? "")}
            onChange={(e) => (onValueChange(e.target.value))}
            {...rest} />
    );
}